import { call, put, select } from 'redux-saga/effects';

import { updatePatient } from 'connect/actions';
import { extractErrorMessage, sendToLogger } from 'connect/saga/helpers';
import { getPatient } from 'connect/saga/saga';
import { selectDataUserAuthenticated } from 'connect/selectors';
import { apiUpdatePatient } from 'endpoints/patient-endpoints';

import Logger from 'utils/Logger';

import { SagaHandler } from '../SagaHandler';

class _UpdatePatientDetails extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/UPDATE_PATIENT_DETAILS',
      stateKey: 'updatePatientDetails',
    });
  }

  createAction({ patientDetails, patientId = null, autoVerify = false }) {
    return {
      type: this._actionName,
      patientDetails,
      patientId,
      autoVerify,
    };
  }

  * saga({ patientDetails, patientId, autoVerify, checkLoginStatus = true }) {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const successHandler = this.success;

    const isLoading = yield select(loadingHandler.selector);
    if (isLoading) {
      return;
    }

    const isUserAuthenticated = yield select(selectDataUserAuthenticated);
    const isUserLoggedIn = !checkLoginStatus || isUserAuthenticated;
    if (!isUserLoggedIn) {
      Logger.error("Tried to update patient details for user that isn't logged in", { patientDetails });
      return;
    }

    const patient = yield call(getPatient);
    if (!patient?.id && !patientId) {
      Logger.error("Tried to update patient details for patient that doesn't exist", { patientDetails });
      return;
    }

    yield put(loadingHandler.update(true));
    yield put(errorHandler.update(null));
    yield put(successHandler.update(null));

    try {
      const updatedPatient = yield call(
        apiUpdatePatient,
        patientId || patient?.id,
        patientDetails,
        autoVerify,
      );

      yield put(updatePatient(updatedPatient));
      yield put(successHandler.update(true));
      return updatedPatient;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      yield put(errorHandler.update(errorMessage));
      sendToLogger(error, 'UpdatePatientDetails');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

export const UpdatePatientDetails = new _UpdatePatientDetails();
